import { useEffect, useLayoutEffect, useRef, useState } from 'react'

import { ApiError } from '~/api/client'
import type { AiLabel } from '~/api/endpoints'
import { Working } from '~/components/working'
import { useWork } from '~/components/use-work'
import { ConfirmDialog } from '~/components/confirm-dialog'
import { Dialog } from '~/components/dialog'
import { Menu, MenuItem } from '~/components/editor-menu'
import { FoodFormDialog } from '~/components/food-form-dialog'
import { CheckIcon, EditIcon, PlusIcon, UploadIcon, VideoIcon } from '~/components/icons'
import { PageDialog } from '~/components/page-dialog'
import { useApi } from '~/data/data-context'
import type { Food } from '~/data/nutrition'
import { downscaleToJpeg } from '~/lib/downscale-image'
import {
  addFurther,
  aimAt,
  cameraAsks,
  canAddFurther,
  canRead,
  emptyTray,
  FURTHER_KINDS,
  land,
  photosOf,
  photosTaken,
  placeName,
  removePhoto,
  type PacketPlace,
  type PacketTray,
} from '~/lib/packet-tray'

/** Where the making stands. */
type Step = 'choosing' | 'photos'

const QUIET = 'rounded-full px-4 py-2 text-sm text-accent transition-colors hover:bg-hover'

const jpeg = (image: string) => `data:image/jpeg;base64,${image}`

/** An animation where the browser plays them; the test runner's does not. */
function play(element: Element | null, frames: Keyframe[], options: KeyframeAnimationOptions) {
  if (element !== null && 'animate' in element) {
    element.animate(frames, options)
  }
}

const stillness = () =>
  typeof window.matchMedia === 'function' &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches

/**
 * Making a food of your own — the nutrition spec, Own foods. Two ways in: typing it, which is
 * the form as it always was, and photographing the packet, which asks the AI to read the label
 * and opens the same form holding what it read, to be looked over before anything is saved.
 *
 * The photographs go into a tray under the camera, a place for each named for what it is of.
 * A nutrition table and the face of a packet answer different questions — what it holds, and
 * what it is called — and the tray is what shows a photo was taken and where it went, which a
 * camera that only changed its caption never did.
 */
export function FoodWizardDialog({
  onClose,
  onSaved,
}: {
  onClose: () => void
  onSaved: (food: Food) => void
}) {
  const api = useApi()
  const [step, setStep] = useState<Step>('choosing')
  const [tray, setTray] = useState<PacketTray>(emptyTray)
  /** What the AI read, which the form opens holding; null for a form filled in by hand. */
  const [label, setLabel] = useState<AiLabel | null>(null)
  const [typing, setTyping] = useState(false)
  /** Why a packet could not be read, in words worth reading. */
  const [trouble, setTrouble] = useState<string | null>(null)
  /** Where leaving would lose photographs taken: the way out asked about, until it is answered. */
  const [leaving, setLeaving] = useState<(() => void) | null>(null)
  /**
   * Which start this is. A reading that comes back after the wizard has been started again is
   * about photographs that were thrown away, and opens nothing over the question.
   */
  const round = useRef(0)

  const startAgain = () => {
    round.current += 1
    setTray(emptyTray())
    setLabel(null)
    setTrouble(null)
    setStep('choosing')
  }

  /** Photographs are something done, so leaving them behind asks first. */
  const leave = (then: () => void) => {
    if (step === 'photos' && photosTaken(tray) > 0) {
      setLeaving(() => then)
    } else {
      then()
    }
  }

  /*
   * Reading waits on the AI, so the key that asked for it works where it stands, over the tray
   * it is reading — the app spec, A press that waits.
   */
  const read = async () => {
    const asked = round.current
    try {
      const got = await api.readLabel(photosOf(tray))
      if (round.current === asked) {
        setLabel(got)
        setTyping(true)
      }
    } catch (error) {
      if (round.current === asked) {
        setTrouble(labelTrouble(error instanceof ApiError ? error.status : null))
      }
    }
  }

  if (typing) {
    return (
      <FoodFormDialog
        seed={label === null ? {} : { label }}
        onClose={() => {
          setTyping(false)
          startAgain()
        }}
        onSaved={onSaved}
      />
    )
  }

  return (
    <PageDialog
      title="New food"
      onClose={() => {
        leave(onClose)
      }}
      className="max-w-md"
      onBack={
        step === 'choosing'
          ? undefined
          : () => {
              leave(startAgain)
            }
      }
    >
      <div className="space-y-4">
        {step === 'choosing' && (
          <div className="space-y-3">
            <h2 className="text-lg font-medium">How would you like to add it?</h2>
            {/* Both say what they are in three words, and neither says any more. */}
            <button
              type="button"
              className="flex w-full items-center gap-3 rounded-2xl bg-surface-raised px-4 py-3 text-left transition-colors hover:bg-hover"
              onClick={() => {
                setStep('photos')
              }}
            >
              <VideoIcon aria-hidden className="size-6 text-ink-muted" />
              Take a photo
            </button>
            <button
              type="button"
              className="flex w-full items-center gap-3 rounded-2xl bg-surface-raised px-4 py-3 text-left transition-colors hover:bg-hover"
              onClick={() => {
                setLabel(null)
                setTyping(true)
              }}
            >
              <EditIcon aria-hidden className="size-6 text-ink-muted" />
              Type it in
            </button>
          </div>
        )}
        {step === 'photos' && <PhotoTray tray={tray} onTray={setTray} onRead={read} />}
      </div>

      {trouble !== null && (
        <ConfirmDialog
          title="That could not be read"
          body={`${trouble} Start again, or type the food in yourself.`}
          confirm="Start again"
          onConfirm={startAgain}
          onDismiss={() => {
            // Whatever the AI could not do, the form can still be filled in by hand.
            setTrouble(null)
            setLabel(null)
            setTyping(true)
          }}
        />
      )}
      {leaving !== null && (
        <ConfirmDialog
          title="Discard these photos?"
          body="The photos taken of this packet will be lost."
          confirm="Discard"
          onConfirm={() => {
            setLeaving(null)
            leaving()
          }}
          onDismiss={() => {
            setLeaving(null)
          }}
        />
      )}
    </PageDialog>
  )
}

/**
 * Why a packet could not be read, in words worth reading. What the server answers in slugs is
 * for us; what stands here has to tell somebody whether to photograph it again, and there is
 * no point sending them back to the camera for an AI that is not set up at all.
 */
function labelTrouble(status: number | null): string {
  if (status === 422) {
    return 'That does not look like a food packet. Try again with the label in frame.'
  }
  if (status === 503) {
    return 'No AI is set up here, so a packet cannot be read.'
  }
  return 'The packet could not be read. It may be the connection.'
}

/**
 * The camera and the tray under it. A photograph is taken with the camera or picked from the
 * device, and lands in the place being filled: the frame flashes and the shot travels into its
 * place, so a press of the shutter is plainly answered.
 */
function PhotoTray({
  tray,
  onTray,
  onRead,
}: {
  tray: PacketTray
  onTray: (next: PacketTray | ((was: PacketTray) => PacketTray)) => void
  onRead: () => Promise<void>
}) {
  const reading = useWork()
  const picking = useWork()
  const snapping = useWork()
  const video = useRef<HTMLVideoElement>(null)
  const frame = useRef<HTMLDivElement>(null)
  const flash = useRef<HTMLDivElement>(null)
  const [lens, setLens] = useState<'opening' | 'open' | 'closed'>('opening')
  const [trouble, setTrouble] = useState<string | null>(null)
  /** The place a photograph has just landed in, and whether it came from the camera's frame. */
  const [arrival, setArrival] = useState<{ id: number; from: DOMRect | null } | null>(null)
  const [looking, setLooking] = useState<PacketPlace | null>(null)
  const aiming = tray.filling !== null

  useEffect(() => {
    const element = video.current
    const devices = navigator.mediaDevices as MediaDevices | undefined
    if (element === null || devices?.getUserMedia === undefined) {
      setLens('closed')
      return
    }
    let stream: MediaStream | null = null
    let alive = true
    devices.getUserMedia({ video: { facingMode: 'environment' } }).then(
      (got) => {
        if (!alive) {
          got.getTracks().forEach((track) => {
            track.stop()
          })
          return
        }
        stream = got
        element.srcObject = got
        void element.play().catch(() => undefined)
        setLens('open')
      },
      () => {
        if (alive) {
          setLens('closed')
        }
      },
    )
    return () => {
      alive = false
      stream?.getTracks().forEach((track) => {
        track.stop()
      })
    }
  }, [])

  const handle = async (blob: Blob | null, from: DOMRect | null) => {
    const target = tray.filling
    if (blob === null || target === null) {
      setTrouble('That photo could not be taken.')
      return
    }
    try {
      const image = await downscaleToJpeg(blob)
      setTrouble(null)
      onTray((was) => (was.filling === target ? land(was, image) : was))
      setArrival({ id: target, from })
    } catch {
      setTrouble('That photo could not be read.')
    }
  }

  const snap = () => {
    const element = video.current
    if (element === null || !aiming || snapping.working) {
      return
    }
    const canvas = document.createElement('canvas')
    canvas.width = element.videoWidth || 1280
    canvas.height = element.videoHeight || 960
    const context = canvas.getContext('2d')
    if (context === null) {
      return
    }
    context.drawImage(element, 0, 0, canvas.width, canvas.height)
    // The shutter answered at once, before the photo has been read down and landed.
    if (!stillness()) {
      play(flash.current, [{ opacity: 0.85 }, { opacity: 0 }], {
        duration: 280,
        easing: 'ease-out',
      })
    }
    const from = frame.current?.getBoundingClientRect() ?? null
    void snapping.run(async () => {
      const blob = await new Promise<Blob | null>((resolve) => {
        canvas.toBlob(resolve, 'image/jpeg')
      })
      await handle(blob, from)
    })
  }

  return (
    <div className="space-y-3">
      <p className="text-sm">{cameraAsks(tray)}</p>
      <div
        ref={frame}
        aria-label="Camera"
        className="relative aspect-[4/3] w-full overflow-hidden rounded-2xl bg-black ring-1 ring-white/10"
      >
        <video
          ref={video}
          autoPlay
          muted
          playsInline
          className={`size-full object-cover ${lens === 'open' ? '' : 'hidden'}`}
        />
        {lens === 'closed' && (
          <p className="absolute inset-0 grid place-items-center px-8 text-center text-sm text-ink-muted">
            The camera could not be opened here.
          </p>
        )}
        <div
          ref={flash}
          aria-hidden
          className="pointer-events-none absolute inset-0 bg-white opacity-0"
        />
      </div>

      <div role="group" aria-label="Photos of the packet" className="flex flex-wrap gap-2">
        {tray.places.map((place) => (
          <TrayPlace
            key={place.id}
            place={place}
            filling={place.id === tray.filling}
            arrival={arrival?.id === place.id ? arrival : null}
            onPress={() => {
              if (place.image === null) {
                onTray((was) => aimAt(was, place.id))
              } else {
                setLooking(place)
              }
            }}
          />
        ))}
        {/*
         * The way to add another, after the last place: offered whatever the tray holds, so a
         * packet photographed only from its side is photographed from the start.
         */}
        {canAddFurther(tray) && (
          <div className="flex w-16 flex-col items-center gap-1 text-xs text-ink-muted">
            <Menu
              label="Add a photo"
              size="size-16 bg-surface-raised"
              icon={<PlusIcon aria-hidden className="size-6" />}
            >
              {FURTHER_KINDS.map((kind) => (
                <MenuItem
                  key={kind}
                  onSelect={() => {
                    onTray((was) => addFurther(was, kind))
                  }}
                >
                  {placeName(kind)}
                </MenuItem>
              ))}
            </Menu>
            Add
          </div>
        )}
      </div>

      <div className="flex items-center justify-between gap-3">
        <label
          className={`${QUIET} relative flex items-center gap-1.5 ${aiming || picking.working ? 'cursor-pointer' : 'pointer-events-none opacity-40'}`}
        >
          <Working shown={picking.shown}>
            <UploadIcon aria-hidden className="size-5" />
            Pick a photo
          </Working>
          <input
            type="file"
            accept="image/*"
            aria-label="Pick a photo"
            aria-busy={picking.working || undefined}
            disabled={!aiming || picking.working}
            className="sr-only"
            onChange={(event) => {
              const file = event.target.files?.[0] ?? null
              event.target.value = ''
              void picking.run(() => handle(file, null))
            }}
          />
        </label>
        {lens === 'open' && (
          <button
            type="button"
            aria-label="Take a photo"
            aria-busy={snapping.working || undefined}
            disabled={!aiming && !snapping.working}
            className="relative grid size-16 place-items-center rounded-full text-white ring-4 ring-white/80 transition-transform active:scale-95 disabled:opacity-40"
            onClick={snap}
          >
            <Working shown={snapping.shown} spinner="size-8">
              <span className="size-12 rounded-full bg-white" />
            </Working>
          </button>
        )}
        <button
          type="button"
          aria-busy={reading.working || undefined}
          disabled={!reading.working && !canRead(tray)}
          className="relative rounded-full bg-accent px-4 py-2 text-sm font-medium text-accent-ink transition-opacity disabled:opacity-40"
          onClick={() => {
            void reading.run(onRead)
          }}
        >
          <Working shown={reading.shown}>Read the packet</Working>
        </button>
      </div>
      {trouble !== null && (
        <p role="status" className="text-center text-sm text-ink-muted">
          {trouble}
        </p>
      )}

      {looking !== null && (
        <Dialog
          label={`${placeName(looking.kind)} photo`}
          onDismiss={() => {
            setLooking(null)
          }}
        >
          {looking.image !== null && (
            <img
              src={jpeg(looking.image)}
              alt={placeName(looking.kind)}
              className="max-h-[60vh] w-full rounded-2xl object-contain"
            />
          )}
          <div className="flex flex-wrap justify-end gap-3">
            <button
              type="button"
              className="rounded-full px-4 py-2 text-sm text-danger"
              onClick={() => {
                onTray((was) => removePhoto(was, looking.id))
                setLooking(null)
              }}
            >
              Remove
            </button>
            <button
              type="button"
              className="rounded-full bg-accent px-4 py-2 text-sm font-medium text-accent-ink"
              onClick={() => {
                onTray((was) => aimAt(was, looking.id))
                setLooking(null)
              }}
            >
              Take again
            </button>
          </div>
        </Dialog>
      )}
    </div>
  )
}

/**
 * One place in the tray: its photograph once there is one, marked done, and the ring that says
 * it is the one being filled. A photograph landing travels in from the camera's frame where it
 * was taken there, and grows into place where it was picked.
 */
function TrayPlace({
  place,
  filling,
  arrival,
  onPress,
}: {
  place: PacketPlace
  filling: boolean
  arrival: { id: number; from: DOMRect | null } | null
  onPress: () => void
}) {
  const picture = useRef<HTMLImageElement>(null)
  const name = placeName(place.kind)
  const image = place.image

  useLayoutEffect(() => {
    const element = picture.current
    if (arrival === null || element === null || image === null || stillness()) {
      return
    }
    const to = element.getBoundingClientRect()
    const from = arrival.from
    const start =
      from === null || to.width === 0
        ? 'scale(0.4)'
        : `translate(${String(from.left - to.left)}px, ${String(from.top - to.top)}px) scale(${String(from.width / to.width)}, ${String(from.height / to.height)})`
    play(
      element,
      [
        { transformOrigin: 'top left', transform: start, opacity: 0.6 },
        { transformOrigin: 'top left', transform: 'none', opacity: 1 },
      ],
      {
        duration: 420,
        easing: 'cubic-bezier(0.2, 0, 0, 1)',
      },
    )
  }, [arrival, image])

  return (
    <button
      type="button"
      aria-label={image === null ? `${name} photo` : `${name} photo, taken`}
      aria-current={filling ? 'true' : undefined}
      className="flex w-16 flex-col items-center gap-1 text-xs"
      onClick={onPress}
    >
      <span
        className={`relative grid size-16 place-items-center rounded-xl bg-surface-raised ring-2 transition-shadow ${filling ? 'ring-accent' : 'ring-transparent'}`}
      >
        {image === null ? (
          <VideoIcon aria-hidden className="size-5 text-ink-muted" />
        ) : (
          <>
            <img
              ref={picture}
              src={jpeg(image)}
              alt=""
              className="size-full rounded-xl object-cover"
            />
            <span className="absolute -right-1 -bottom-1 grid size-5 place-items-center rounded-full bg-accent text-accent-ink">
              <CheckIcon aria-hidden className="size-3.5" />
            </span>
          </>
        )}
      </span>
      <span className={filling ? 'text-ink' : 'text-ink-muted'}>{name}</span>
    </button>
  )
}
